import { LitElement, css, html, nothing, unsafeCSS } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { createRef, ref } from 'lit/directives/ref.js'
import sheet from './css/bb-menu.css?inline'
import './bb-menu-item'
import type { BerryBotApi, RawMenuItem } from './bbcore/api'

interface ModifierBadge {
  id: string
  name: string
  required: boolean
  // 'adjustments' = removable ingredients group; everything else with
  // options the customer picks from we treat as a 'choices' group.
  groupType: 'choices' | 'adjustments'
  // Pick-range for the picker (min=0 + max=1 = optional toggle; min=1 +
  // max=1 = required radio; max>1 = checkbox multi-select up to max).
  minSelections: number
  maxSelections: number
  options: Array<{ name: string; modifierType?: string }>
}

interface MenuItem {
  id: string
  name: string
  description: string
  category: string
  price: string
  imageUrl: string | null
  available: boolean
  snoozedUntil: string | null
  modifierGroups: ModifierBadge[]
}

function priceText(price: RawMenuItem['price']): string {
  // PriceSerializer ships `{fractional, formatted: "R36.00"}` — prefer the
  // server-formatted string (multi-currency-correct) and only fall back to
  // a manual R-prefixed render if the formatted field is missing.
  const formatted = price?.formatted?.trim()
  if (formatted) return formatted
  const cents = price?.fractional
  return cents == null ? 'R0.00' : `R${(cents / 100).toFixed(2)}`
}

function toMenuItem(it: RawMenuItem): MenuItem | null {
  const name = (it.name || '').trim()
  if (!name) return null
  return {
    id: it.id,
    name,
    description: it.description || '',
    category: it.categories?.[0]?.name?.trim() || 'Other',
    price: priceText(it.price),
    imageUrl: typeof it.item_image === 'string' && it.item_image ? it.item_image : null,
    // Default to available when the server didn't tell us either way —
    // /menus/:id without `storefront_id` returns no snooze data, so an
    // absent field is "not snoozed", not "unknown".
    available: it.available !== false,
    snoozedUntil: typeof it.snoozed_until === 'string' ? it.snoozed_until : null,
    modifierGroups: (it.modifier_groups ?? [])
      .map((g) => ({
        id: g.id ?? '',
        name: (g.name ?? '').trim(),
        required: Number(g.min_quantity ?? 0) >= 1,
        groupType:
          g.group_type === 'adjustments'
            ? ('adjustments' as const)
            : ('choices' as const),
        minSelections: Number(g.min_quantity ?? 0),
        maxSelections: Number(g.max_quantity ?? 0),
        options: (g.option_names ?? [])
          .map((n, i) => {
            const name = n.trim()
            const type = (g.option_types ?? [])[i]?.trim() || undefined
            return name ? { name, modifierType: type } : null
          })
          .filter((o): o is { name: string; modifierType?: string } => o !== null),
      }))
      .filter((g) => g.name.length > 0 && g.id.length > 0),
  }
}

@customElement('bb-menu')
export class BbMenu extends LitElement {
  static styles = css`${unsafeCSS(sheet)}`

  @property({ attribute: false })
  api: BerryBotApi | null = null

  @property({ attribute: 'menu-id' })
  menuId = ''

  // Required for per-storefront snooze state on /menus/:id. Without it the
  // server reports every item available; the panel still renders.
  @property({ attribute: 'storefront-id' })
  storefrontId = ''

  @state() private items: MenuItem[] = []
  @state() private categories: string[] = []
  @state() private loading = true
  @state() private error: string | null = null
  // Live menu search — non-empty query matches `name` substring across the
  // whole menu and surfaces results in a dropdown; picking one scrolls the
  // stacked list to that item rather than filtering the list down.
  @state() private search = ''
  @state() private searchOpen = false
  // Category whose section is currently scrolled into view. Drives the active
  // chip highlight (scroll-spy) and is set directly on a chip / jump click.
  @state() private active: string | null = null
  // True when the chip strip is wider than its container — surfaces a "More"
  // jump menu listing every category. Detected from scroll vs client width,
  // so there's no per-chip measurement to drift.
  @state() private overflowing = false
  @state() private moreOpen = false
  // Category whose items are mid-flash after a chip click — drives a
  // transient outline pulse on the matching .item-wraps so the customer
  // visually registers what they just scrolled to. `_flashKey` bumps
  // each click so re-clicking the same category restarts the animation
  // (class string changes → Lit re-applies → CSS animation re-runs).
  @state() private _flashCategory: string | null = null
  @state() private _flashKey = 0
  private _flashTimer?: ReturnType<typeof setTimeout>

  private itemsRef = createRef<HTMLElement>()
  private navRef = createRef<HTMLElement>()
  private _resizeObs?: ResizeObserver
  private _spyObs?: IntersectionObserver
  private _onDocClick = (e: MouseEvent) => {
    // Close search / more-menu on any click outside their own region —
    // including clicks elsewhere inside bb-menu (a category chip, a menu
    // item). The toggle buttons stopPropagation, so the document handler
    // only fires for clicks the toggles didn't originate.
    const path = e.composedPath()
    const inside = (cls: string) =>
      path.some(
        (n) => n instanceof HTMLElement && n.classList.contains(cls),
      )
    if (!inside('search-toggle') && !inside('search-panel')) {
      this.searchOpen = false
    }
    if (!inside('more-wrap')) this.moreOpen = false
  }

  connectedCallback() {
    super.connectedCallback()
    void this.load()
    document.addEventListener('click', this._onDocClick)
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    document.removeEventListener('click', this._onDocClick)
    this._resizeObs?.disconnect()
    this._spyObs?.disconnect()
  }

  updated(changed: Map<string, unknown>) {
    if (
      (changed.has('api') || changed.has('menuId') || changed.has('storefrontId')) &&
      this.api &&
      this.menuId &&
      this.items.length === 0 &&
      !this.error
    ) {
      void this.load()
      return
    }
    // Items just (re)rendered: wire scroll-spy + overflow detection to the
    // fresh DOM, recomputing whenever the category set changes.
    if (changed.has('items') && this.items.length) {
      this.setupSpy()
      this.observeNav()
      this.measureNav()
    }
    // Keep the highlighted chip visible as the body scrolls past sections.
    // Skip the initial null → first-category set on mount: when the widget
    // is embedded in a cross-origin iframe (marketing site's demo preview),
    // scrollIntoView propagates up through every ancestor scroll container
    // and drags the parent page down to bring the chip into view — same
    // hazard bb-chat's focus() gate warns about, no preventScroll option
    // on scrollIntoView.
    if (changed.has('active') && this.active && changed.get('active') != null) {
      const i = this.categories.indexOf(this.active)
      const chip = this.navRef.value?.querySelectorAll('.chip')[i] as
        | HTMLElement
        | undefined
      if (typeof chip?.scrollIntoView === 'function')
        chip.scrollIntoView({ block: 'nearest', inline: 'nearest' })
    }
    // Focus the field the moment search opens.
    if (changed.has('searchOpen') && this.searchOpen) {
      this.renderRoot
        .querySelector<HTMLInputElement>('input.search')
        ?.focus()
    }
  }

  private async load() {
    if (!this.api || !this.menuId) return
    this.loading = true
    this.error = null
    try {
      const raw = await this.api.getMenu(this.menuId, this.storefrontId || undefined)
      const shaped = raw
        .map(toMenuItem)
        .filter((x): x is MenuItem => x !== null)
      this.items = shaped
      this.categories = Array.from(
        new Set(shaped.map((s) => s.category)),
      )
      this.active = this.categories[0] ?? null
    } catch (e) {
      this.error = (e as Error).message
    } finally {
      this.loading = false
    }
  }

  // ── scroll-spy ────────────────────────────────────────────────────────
  // Highlights the chip for whichever section sits in the top third of the
  // scroll container, so the strip tracks free scrolling, not just clicks.
  private setupSpy() {
    this._spyObs?.disconnect()
    const root = this.itemsRef.value
    if (!root || typeof IntersectionObserver === 'undefined') return
    this._spyObs = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting)
        if (visible.length === 0) return
        visible.sort(
          (a, b) => a.boundingClientRect.top - b.boundingClientRect.top,
        )
        // Observed element is the first .item-wrap of a section; walk
        // up to the section to read data-idx (the wrap itself doesn't
        // carry one since it lives in a per-category section).
        const target = visible[0].target as HTMLElement
        const sec = target.closest('.section') as HTMLElement | null
        const idx = Number(sec?.dataset.idx ?? -1)
        const cat = this.categories[idx]
        if (cat && cat !== this.active) this.active = cat
      },
      { root, rootMargin: '0px 0px -70% 0px', threshold: 0 },
    )
    // Section headings are hidden in the flat-grid layout (no layout
    // box, so IntersectionObserver never fires). Observe each section's
    // first .item-wrap instead — its data-category-idx tells the spy
    // which category boundary just scrolled into view.
    root
      .querySelectorAll('.section')
      .forEach((sec) => {
        const first = sec.querySelector('.item-wrap')
        if (first) this._spyObs!.observe(first)
      })
  }

  private observeNav() {
    if (
      this._resizeObs ||
      !this.navRef.value ||
      typeof ResizeObserver === 'undefined'
    )
      return
    this._resizeObs = new ResizeObserver(() => this.measureNav())
    this._resizeObs.observe(this.navRef.value)
  }

  private measureNav() {
    const nav = this.navRef.value
    if (!nav) return
    const over = nav.scrollWidth > nav.clientWidth + 1
    if (over !== this.overflowing) this.overflowing = over
  }


  private jumpTo = (cat: string) => {
    this.active = cat
    this.moreOpen = false
    // Kick the flash. Bumping _flashKey forces a fresh class string
    // even on a repeat-click of the same category so the CSS animation
    // restarts. Clear after the animation window so the class drops
    // off and pointer:hover styles take over normally.
    this._flashCategory = cat
    this._flashKey++
    if (this._flashTimer) clearTimeout(this._flashTimer)
    this._flashTimer = setTimeout(() => {
      this._flashCategory = null
      this._flashTimer = undefined
    }, 4100)
    const idx = this.categories.indexOf(cat)
    const root = this.itemsRef.value
    // Anchor on .section-heading so its label stays visible — the heading
    // is a real grid child (Trello #134); .section itself uses
    // display:contents and has no offsetTop.
    const heading = root?.querySelector(
      `.section[data-idx="${idx}"] .section-heading`,
    ) as HTMLElement | null
    if (root && heading && typeof root.scrollTo === 'function')
      root.scrollTo({ top: heading.offsetTop - 4, behavior: 'smooth' })
  }

  // A search result is a fast path to *order* the item — same contract as a
  // tile click (emit `item-selected`); the host turns it into an utterance.
  // Picking one closes search rather than locating it in the list.
  // Unavailable matches are rendered for visibility but ignore the click,
  // mirroring how tiles behave for snoozed/86'd items.
  private onSearchPick = (it: MenuItem) => {
    if (!it.available) return
    this.onPick(it)
    this.search = ''
    this.searchOpen = false
  }

  private get searchMatches(): MenuItem[] {
    const q = this.search.trim().toLowerCase()
    if (!q) return []
    const seen = new Set<string>()
    const out: MenuItem[] = []
    for (const it of this.items) {
      if (!it.name.toLowerCase().includes(q) || seen.has(it.name)) continue
      seen.add(it.name)
      out.push(it)
      if (out.length >= 8) break
    }
    return out
  }

  private onPick(item: MenuItem) {
    if (!item.available) return
    this.dispatchEvent(
      new CustomEvent('widget-affordance', {
        detail: { kind: 'menu-tile', context: { item: item.name } },
        bubbles: true,
        composed: true,
      }),
    )
    this.dispatchEvent(
      new CustomEvent('item-selected', {
        detail: { item },
        bubbles: true,
        composed: true,
      }),
    )
  }

  private onSearchInput = (e: Event) => {
    this.search = (e.target as HTMLInputElement).value
  }

  private toggleMore = (e: Event) => {
    e.stopPropagation()
    this.moreOpen = !this.moreOpen
    this.searchOpen = false
  }

  private toggleSearch = (e: Event) => {
    e.stopPropagation()
    this.searchOpen = !this.searchOpen
    this.moreOpen = false
    if (!this.searchOpen) this.search = ''
  }

  render() {
    if (this.loading) {
      return html`<div class="bar"></div><div class="items"><div class="placeholder">Loading menu…</div></div>`
    }
    if (this.error) {
      return html`<div class="bar"></div><div class="items"><div class="placeholder">Menu unavailable.</div></div>`
    }
    if (this.items.length === 0) {
      return html`<div class="bar"></div><div class="items"><div class="placeholder">Empty menu.</div></div>`
    }
    return html`
      <div class="bar">
        <nav class="chips" ${ref(this.navRef)}>
          ${this.categories.map(
            (cat) => html`
              <button
                class="chip ${cat === this.active ? 'active' : ''}"
                @click=${() => this.jumpTo(cat)}
              >
                ${cat}
              </button>
            `,
          )}
        </nav>
        ${this.overflowing
          ? html`
              <div class="more-wrap">
                <button
                  class="icon-btn more ${this.moreOpen ? 'active' : ''}"
                  aria-label="All categories"
                  @click=${this.toggleMore}
                >
                  ⋯
                </button>
                ${this.moreOpen
                  ? html`
                      <div class="menu" part="menu-categories">
                        ${this.categories.map(
                          (cat) => html`
                            <button @click=${() => this.jumpTo(cat)}>
                              ${cat}
                            </button>
                          `,
                        )}
                      </div>
                    `
                  : nothing}
              </div>
            `
          : nothing}
        <button
          class="icon-btn search-toggle ${this.searchOpen ? 'active' : ''}"
          aria-label="Search menu"
          @click=${this.toggleSearch}
        >
          🔍
        </button>
      </div>
      ${this.searchOpen
        ? html`
            <div class="search-panel">
              <input
                class="search"
                type="search"
                .value=${this.search}
                @input=${this.onSearchInput}
                placeholder="Search menu…"
                aria-label="Search menu"
              />
              ${this.search.trim()
                ? html`
                    <div class="search-results" part="menu-search-results">
                      ${this.searchMatches.length === 0
                        ? html`<div class="empty">No match. Try a different word.</div>`
                        : this.searchMatches.map(
                            (it) => html`
                              <div
                                class="res${it.available ? '' : ' unavailable'}"
                                @click=${() => this.onSearchPick(it)}
                              >
                                <span class="nm">${it.name}</span>
                                ${it.available
                                  ? nothing
                                  : html`<span class="oos-badge">Currently unavailable</span>`}
                                <span class="pr">${it.price}</span>
                              </div>
                            `,
                          )}
                    </div>
                  `
                : nothing}
            </div>
          `
        : nothing}
      <div class="items" ${ref(this.itemsRef)}>
        ${this.categories.map(
          (cat, idx) => html`
            <div class="section" data-idx=${idx} data-category=${cat}>
              <h3 class="section-heading">${cat}</h3>
              ${this.items
                .filter((i) => i.category === cat)
                .map((it) => this.renderItem(it, cat))}
            </div>
          `,
        )}
      </div>
    `
  }

  private renderItem(it: MenuItem, cat: string) {
    // Snoozed/unavailable items render dimmed with a badge and ignore clicks.
    // The agent would 400 the cart/add anyway; pre-filtering here saves a
    // round-trip and makes the state obvious to the customer.
    // data-category is the stable hook for tests / per-category lookups —
    // independent of the heading element and resilient to any future
    // layout reshuffling.
    const flashClass = this._flashCategory === cat
      ? ` flash flash-${this._flashKey}`
      : ''
    return html`
      <div
        class="item-wrap${it.available ? '' : ' unavailable'}${flashClass}"
        data-category=${cat}
      >
        <bb-menu-item
          .name=${it.name}
          .description=${it.description}
          .price=${it.price}
          .imageUrl=${it.imageUrl}
          .category=${it.category}
          .modifierGroups=${it.modifierGroups.map((g) => ({
            name: g.name,
            required: g.required,
            groupType: g.groupType,
          }))}
          ?disabled=${!it.available}
          @item-pick=${() => this.onPick(it)}
        ></bb-menu-item>
        ${it.available
          ? nothing
          : html`<span class="unavailable-badge">Currently unavailable</span>`}
      </div>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'bb-menu': BbMenu
  }
}
